import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { AddressAutocomplete } from '@/components/ui/address-autocomplete';
import {
  Loader2, Clock, MapPin, Users, TrendingUp, Search, ChevronLeft, ChevronRight,
  Truck, CalendarDays, ArrowUpDown, Zap, FileCheck, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn, localToday } from '@/lib/utils';
import { AvailabilityCheckPanel } from '@/components/scheduling/AvailabilityCheckPanel';
import { PendingDraftsPanel } from '@/components/scheduling/PendingDraftsPanel';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  low:        { label: 'Low',        color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  moderate:   { label: 'Moderate',   color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  busy:       { label: 'Busy',       color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  full:       { label: 'Full',       color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  overloaded: { label: 'Overloaded', color: 'bg-red-600/20 text-red-300 border-red-600/40' },
};

function getMonday(d) {
  const date = new Date(String(d).slice(0, 10) + 'T12:00:00');
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(date.setDate(diff));
}

function formatDate(str) {
  const d = new Date(str + 'T00:00:00');
  return d.toLocaleDateString('ru-RU', { weekday: 'short', day: 'numeric', month: 'short' });
}

// ====== LOAD BAR ======
function LoadBar({ percent, status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.low;
  const width = Math.min(percent, 100);
  return (
    <div className="w-full" data-testid="load-bar">
      <div className="flex justify-between text-xs mb-1">
        <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0', cfg.color)}>{cfg.label}</Badge>
        <span className="text-muted-foreground font-mono">{percent}%</span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all duration-500',
            percent <= 40 ? 'bg-emerald-500' :
            percent <= 65 ? 'bg-amber-500' :
            percent <= 85 ? 'bg-orange-500' : 'bg-red-500'
          )}
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
}

// ====== WORKER LOAD CARD ======
function WorkerLoadCard({ worker }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <Card
      className="cursor-pointer hover:border-primary/40 transition-colors"
      onClick={() => setExpanded(!expanded)}
      data-testid={`worker-load-card-${worker.worker_id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: worker.worker_color }} />
          <span className="font-medium text-sm">{worker.worker_name}</span>
          <span className="ml-auto text-xs text-muted-foreground font-mono">
            {Math.round(worker.weekly_occupied_minutes / 60)}h / {Math.round(worker.weekly_capacity_minutes / 60)}h
          </span>
        </div>
        <LoadBar percent={worker.weekly_load_percent} status={worker.weekly_load_percent <= 40 ? 'low' : worker.weekly_load_percent <= 65 ? 'moderate' : worker.weekly_load_percent <= 85 ? 'busy' : 'full'} />
        <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Truck className="h-3 w-3" />{Math.floor(worker.travel_total_minutes / 60)}h {Math.round(worker.travel_total_minutes % 60)}m travel</span>
          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{Math.floor(worker.remaining_minutes / 60)}h {Math.round(worker.remaining_minutes % 60)}m free</span>
        </div>

        {expanded && (
          <div className="mt-4 space-y-2 border-t pt-3" data-testid="worker-days-detail">
            {worker.days.map(d => (
              <div key={d.date} className="flex items-center gap-2 text-xs">
                <span className="w-24 text-muted-foreground">{formatDate(d.date)}</span>
                <div className="flex-1"><LoadBar percent={d.load_percent} status={d.status} /></div>
                <span className="w-8 text-right text-muted-foreground">{d.jobs_count}j</span>
                <span className="w-16 text-right text-muted-foreground">{d.largest_free_slot_minutes}m free</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ====== SORT OPTIONS ======
const SORT_OPTIONS = [
  { value: 'load_asc', label: 'Lowest load' },
  { value: 'remaining_desc', label: 'Most remaining time' },
  { value: 'free_slot_desc', label: 'Largest free slot' },
  { value: 'travel_asc', label: 'Lowest travel' },
  { value: 'jobs_asc', label: 'Fewest jobs' },
];

function sortWorkers(workers, sortBy) {
  const sorted = [...workers];
  switch (sortBy) {
    case 'load_asc': return sorted.sort((a, b) => a.weekly_load_percent - b.weekly_load_percent);
    case 'remaining_desc': return sorted.sort((a, b) => b.remaining_minutes - a.remaining_minutes);
    case 'free_slot_desc': return sorted.sort((a, b) => {
      const aMax = Math.max(...a.days.map(d => d.largest_free_slot_minutes));
      const bMax = Math.max(...b.days.map(d => d.largest_free_slot_minutes));
      return bMax - aMax;
    });
    case 'travel_asc': return sorted.sort((a, b) => a.travel_total_minutes - b.travel_total_minutes);
    case 'jobs_asc': return sorted.sort((a, b) => {
      const aJobs = a.days.reduce((s, d) => s + d.jobs_count, 0);
      const bJobs = b.days.reduce((s, d) => s + d.jobs_count, 0);
      return aJobs - bJobs;
    });
    default: return sorted;
  }
}

// ====== COMPLETE ORDER FROM PENDING DRAFT DIALOG ======
function CompletePendingOrderDialog({ open, onOpenChange, api, draft, onCompleted }) {
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [clientId, setClientId] = useState('');
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  const [description, setDescription] = useState('');
  const [totalPrice, setTotalPrice] = useState('');
  const [loading, setLoading] = useState(false);
  const [showNewClient, setShowNewClient] = useState(false);
  const [showPriceConfirm, setShowPriceConfirm] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', phone: '', email: '', address: '', lat: null, lng: null });
  const [creatingClient, setCreatingClient] = useState(false);
  const [clientSearch, setClientSearch] = useState('');

  useEffect(() => {
    if (open && draft) {
      // Reset state first
      setClientId('');
      setSelectedServiceIds([]);
      setDescription('');
      setTotalPrice('');
      setShowNewClient(false);
      setShowPriceConfirm(false);
      
      // Pre-fill from draft
      const clientName = draft.title || '';
      setClientSearch(clientName);
      
      // Pre-fill new client form with draft data
      setNewClient({
        name: clientName,
        phone: '',
        email: '',
        address: draft.address_full || '',
        lat: draft.address_lat || null,
        lng: draft.address_lng || null
      });
      
      // Pre-fill service type if available
      if (draft.service_type_id) {
        setSelectedServiceIds([draft.service_type_id]);
      }
      
      // Load clients and try to find match
      api.get('/clients?limit=200').then(r => {
        const clientsList = Array.isArray(r.data) ? r.data : [];
        setClients(clientsList);
        
        // Try to find exact match by name
        if (clientName) {
          const exactMatch = clientsList.find(c => c.name?.toLowerCase() === clientName.toLowerCase());
          if (exactMatch) {
            setClientId(exactMatch.id);
            setClientSearch(exactMatch.name);
          }
        }
      }).catch(() => {});
      
      api.get('/service-types').then(r => setServices(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    }
  }, [open, api, draft]);
  
  // Filter clients based on search
  const filteredClients = clients.filter(c => 
    !clientSearch || c.name?.toLowerCase().includes(clientSearch.toLowerCase())
  );

  const handleCreateClient = async () => {
    // Frontend validation before sending
    if (!newClient.name || newClient.name.trim().length < 2) {
      return toast.error('Имя: минимум 2 символа');
    }
    if (!newClient.phone || newClient.phone.replace(/\D/g, '').length < 10) {
      return toast.error('Телефон: минимум 10 цифр');
    }
    if (!newClient.email || !newClient.email.includes('@') || newClient.email.length < 5) {
      return toast.error('Email: введите корректный email с @');
    }
    if (!newClient.address || newClient.address.trim().length < 5) {
      return toast.error('Адрес: минимум 5 символов');
    }
    
    setCreatingClient(true);
    try {
      const resp = await api.post('/clients', {
        name: newClient.name,
        phone: newClient.phone,
        email: newClient.email,
        address: { 
          full: newClient.address,
          lat: newClient.lat,
          lng: newClient.lng,
        },
      });
      const created = resp.data;
      setClients(prev => [created, ...prev]);
      setClientId(created.id);
      setClientSearch(created.name);
      setShowNewClient(false);
      setNewClient({ name: '', phone: '', email: '', address: '', lat: null, lng: null });
      toast.success(`Клиент "${created.name}" создан`);
    } catch (err) {
      // Handle validation errors from backend
      const detail = err.response?.data?.detail;
      if (detail?.errors && Array.isArray(detail.errors)) {
        toast.error(detail.errors.join('\n'));
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Ошибка при создании клиента');
      }
    } finally {
      setCreatingClient(false);
    }
  };

  const addService = (id) => {
    if (id && !selectedServiceIds.includes(id)) setSelectedServiceIds(prev => [...prev, id]);
  };
  const removeService = (id) => {
    setSelectedServiceIds(prev => prev.filter(s => s !== id));
  };

  const handleSubmit = () => {
    if (!clientId || !draft) return toast.error('Select a client');
    setShowPriceConfirm(true);
  };

  const handleConfirmOrder = async () => {
    setShowPriceConfirm(false);
    setLoading(true);
    try {
      const resp = await api.post(`/schedule/drafts/${draft.id}/complete-order`, {
        client_id: clientId,
        service_type_ids: selectedServiceIds.length > 0 ? selectedServiceIds : undefined,
        description,
        total_price: parseFloat(totalPrice) || 0,
        address_street: draft.address_full || '',
      });
      toast.success('Order created successfully');
      onCompleted(resp.data);
      onOpenChange(false);
    } catch (err) {
      // FE-01 FIX: More specific error message based on error type
      console.error('Create order error:', err);
      const errorMsg = err.response?.data?.detail;
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        toast.error('Request timed out. Please check if the order was created.');
      } else if (!err.response) {
        toast.error('Network error. Please check your connection and verify if order was created.');
      } else {
        toast.error(errorMsg || 'Failed to create order');
      }
    } finally {
      setLoading(false);
    }
  };

  const priceNum = parseFloat(totalPrice) || 0;

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-auto bottom-0 translate-x-0 translate-y-0 sm:top-[50%] sm:translate-y-[-50%] sm:translate-x-[-50%] sm:bottom-auto left-0 right-0 sm:left-[50%] sm:right-auto w-full sm:w-auto sm:max-w-md rounded-t-2xl rounded-b-none sm:rounded-lg max-h-[90dvh] overflow-y-auto p-4 sm:p-6" data-testid="complete-pending-order-dialog">
        <DialogHeader className="pb-2">
          <DialogTitle className="flex items-center gap-2 text-base">
            <FileCheck className="h-4 w-4" /> Complete Order
          </DialogTitle>
        </DialogHeader>
        {draft && (
          <div className="p-2 rounded bg-muted/50 text-xs space-y-1 mb-2">
            <div className="font-medium truncate">{draft.title}</div>
            <div className="text-muted-foreground">
              {draft.locked_date && formatDate(draft.locked_date)}
              {draft.locked_time_start && ` ${draft.locked_time_start}-${draft.locked_time_end}`}
            </div>
          </div>
        )}
        <div className="space-y-3">
          {/* CLIENT */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs">Client *</Label>
              <Button variant="ghost" size="sm" className="h-6 text-xs text-blue-400 hover:text-blue-300 px-2"
                onClick={() => setShowNewClient(!showNewClient)} data-testid="pending-toggle-new-client">
                {showNewClient ? 'Cancel' : '+ New'}
              </Button>
            </div>
            {showNewClient ? (
              <div className="space-y-2 p-2 rounded border border-blue-500/20 bg-blue-500/5">
                <Input placeholder="Name (Имя Фамилия) *" value={newClient.name} onChange={e => setNewClient(p => ({...p, name: e.target.value}))} className="h-10 text-base" />
                <Input placeholder="Phone: +1 604 123 4567 *" type="tel" value={newClient.phone} onChange={e => setNewClient(p => ({...p, phone: e.target.value}))} className="h-10 text-base" />
                <Input placeholder="Email *" type="email" value={newClient.email} onChange={e => setNewClient(p => ({...p, email: e.target.value}))} className="h-10 text-base" />
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Address *</Label>
                  <AddressAutocomplete
                    value={newClient.address}
                    onChange={(val) => setNewClient(p => ({...p, address: val}))}
                    onSelect={(result) => setNewClient(p => ({
                      ...p, 
                      address: result.address, 
                      lat: result.lat, 
                      lng: result.lng
                    }))}
                    placeholder="Start typing address..."
                    className="h-10 text-base"
                  />
                  {newClient.lat && (
                    <p className="text-[10px] text-emerald-400 px-1">✓ Address verified</p>
                  )}
                </div>
                <Button size="sm" className="w-full h-10" onClick={handleCreateClient} 
                  disabled={creatingClient || !newClient.name || !newClient.phone || !newClient.email || !newClient.address}>
                  {creatingClient ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                  Create Client
                </Button>
              </div>
            ) : (
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Type to search clients..."
                  value={clientSearch}
                  onChange={e => {
                    setClientSearch(e.target.value);
                    if (clientId) setClientId('');
                  }}
                  className="h-10 text-base"
                  data-testid="pending-complete-client-search"
                />
                {clientId && (
                  <div className="mt-1 p-2 rounded bg-green-500/10 border border-green-500/20 flex items-center justify-between">
                    <span className="text-sm font-medium text-green-400">
                      ✓ {clients.find(c => c.id === clientId)?.name}
                    </span>
                    <button onClick={() => { setClientId(''); setClientSearch(''); }} className="text-xs text-muted-foreground hover:text-red-400">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
                {!clientId && clientSearch && filteredClients.length > 0 && (
                  <div className="absolute z-50 mt-1 w-full bg-background border rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {filteredClients.slice(0, 10).map(c => (
                      <button
                        key={c.id}
                        onClick={() => {
                          setClientId(c.id);
                          setClientSearch(c.name);
                        }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-muted border-b last:border-b-0"
                      >
                        {c.name}
                        {c.address?.full && <span className="text-xs text-muted-foreground ml-2">({c.address.full.slice(0, 30)}...)</span>}
                      </button>
                    ))}
                  </div>
                )}
                {!clientId && clientSearch && filteredClients.length === 0 && (
                  <div className="mt-1 p-2 text-xs text-muted-foreground">
                    No clients found. Click "+ New" to create one.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* SERVICES */}
          <div>
            <Label className="text-xs mb-1 block">Services</Label>
            {selectedServiceIds.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {selectedServiceIds.map(sid => {
                  const svc = services.find(s => s.id === sid);
                  return (
                    <span key={sid} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-primary/10 border border-primary/20">
                      {svc?.name || sid}
                      <button onClick={() => removeService(sid)} className="hover:text-red-400 ml-0.5">&times;</button>
                    </span>
                  );
                })}
              </div>
            )}
            <Select value="" onValueChange={addService}>
              <SelectTrigger className="h-10 text-base" data-testid="pending-complete-service-select">
                <SelectValue placeholder="+ Add service..." />
              </SelectTrigger>
              <SelectContent>
                {services.filter(s => !selectedServiceIds.includes(s.id)).map(s => (
                  <SelectItem key={s.id} value={s.id} className="text-sm">{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* DESCRIPTION */}
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Order details..." className="text-base min-h-[60px]" data-testid="pending-complete-description" />
          </div>

          {/* TOTAL PRICE */}
          <div>
            <Label className="text-xs">Total Price ($)</Label>
            <Input type="number" inputMode="decimal" min="0" step="0.01" value={totalPrice} onChange={e => setTotalPrice(e.target.value)}
              placeholder="0.00" className="h-10 text-base font-medium" data-testid="pending-complete-total-price" />
            {(totalPrice === '' || priceNum === 0) && (
              <p className="text-xs text-amber-400 mt-1">If price is $0, a reminder will appear on the dashboard</p>
            )}
          </div>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2 pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto h-10">Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading || !clientId} className="w-full sm:w-auto h-10" data-testid="pending-complete-submit-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileCheck className="h-4 w-4 mr-2" />}
            Create Order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* PRICE CONFIRMATION */}
    <Dialog open={showPriceConfirm} onOpenChange={setShowPriceConfirm}>
      <DialogContent className="top-auto bottom-0 translate-x-0 translate-y-0 sm:top-[50%] sm:translate-y-[-50%] sm:translate-x-[-50%] sm:bottom-auto left-0 right-0 sm:left-[50%] sm:right-auto w-full sm:max-w-sm rounded-t-2xl rounded-b-none sm:rounded-lg p-4 sm:p-6" data-testid="pending-price-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="text-center">Confirm Price</DialogTitle>
        </DialogHeader>
        <div className="text-center py-4 space-y-3">
          <p className="text-sm text-muted-foreground">Please verify the order total:</p>
          <div className="text-4xl sm:text-5xl font-bold tracking-tight" style={{ color: priceNum > 0 ? '#22c55e' : '#ef4444' }}>
            ${priceNum.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          {priceNum === 0 && <p className="text-amber-400 text-sm font-medium">Price is $0 — a dashboard reminder will be created</p>}
          <p className="text-sm text-muted-foreground">Is this the correct amount?</p>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" className="w-full sm:flex-1 h-10" onClick={() => setShowPriceConfirm(false)}>
            No, Edit Price
          </Button>
          <Button className="w-full sm:flex-1 h-10 bg-green-600 hover:bg-green-700" onClick={handleConfirmOrder} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Yes, Create Order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

// ====== MAIN PAGE ======
export default function SchedulingPage() {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [weekStart, setWeekStart] = useState(() => {
    const m = getMonday(new Date());
    return localToday(m);
  });
  const [sortBy, setSortBy] = useState('load_asc');
  const [refreshKey, setRefreshKey] = useState(0);
  const [pendingDraft, setPendingDraft] = useState(null);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);

  const weekEnd = useMemo(() => {
    const d = new Date(weekStart + 'T12:00:00');
    d.setDate(d.getDate() + 6);
    return localToday(d);
  }, [weekStart]);

  const fetchLoad = async () => {
    setLoading(true);
    try {
      const resp = await api.get(`/workers/load?week_start=${weekStart}&week_end=${weekEnd}`);
      setData(resp.data);
    } catch (err) {
      toast.error('Failed to load worker data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLoad(); }, [weekStart]);

  const shiftWeek = (dir) => {
    const d = new Date(weekStart + 'T12:00:00');
    d.setDate(d.getDate() + dir * 7);
    setWeekStart(localToday(d));
  };

  const sortedWorkers = useMemo(() => {
    if (!data?.workers) return [];
    return sortWorkers(data.workers, sortBy);
  }, [data, sortBy]);

  const summary = useMemo(() => {
    if (!data?.workers?.length) return null;
    const total = data.workers.length;
    const avgLoad = Math.round(data.workers.reduce((s, w) => s + w.weekly_load_percent, 0) / total);
    const totalTravel = Math.round(data.workers.reduce((s, w) => s + w.travel_total_minutes, 0));
    const totalJobs = data.workers.reduce((s, w) => s + w.days.reduce((ds, d) => ds + d.jobs_count, 0), 0);
    return { total, avgLoad, totalTravel, totalJobs };
  }, [data]);

  const handleDraftCreated = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  const handleContinueDraft = (draft) => {
    toast.info(`Draft "${draft.title}" — use the Check Availability panel above to re-search or select a new slot.`);
  };

  const handleCompletePendingOrder = (draft) => {
    setPendingDraft(draft);
    setCompleteDialogOpen(true);
  };

  const handlePendingOrderCompleted = () => {
    setRefreshKey(k => k + 1);
    setPendingDraft(null);
    fetchLoad();
  };

  return (
    <div className="space-y-6" data-testid="scheduling-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Team Load & Scheduling</h1>
          <p className="text-sm text-muted-foreground">Worker capacity, travel time & intelligent slot recommendation</p>
        </div>
      </div>

      {/* Availability Check Panel */}
      <AvailabilityCheckPanel api={api} onDraftCreated={handleDraftCreated} />

      {/* Pending Drafts */}
      <PendingDraftsPanel
        api={api}
        refreshKey={refreshKey}
        onContinue={handleContinueDraft}
        onCompleteOrder={handleCompletePendingOrder}
      />

      {/* Week nav */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => shiftWeek(-1)} data-testid="prev-week-btn">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium text-center" data-testid="week-range">
            {formatDate(weekStart)} — {formatDate(weekEnd)}
          </span>
          <Button variant="outline" size="icon" onClick={() => shiftWeek(1)} data-testid="next-week-btn">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setWeekStart(localToday(getMonday(new Date())))}>
            Today
          </Button>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map(o => (
                <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-4 gap-3">
          <Card><CardContent className="p-3 text-center">
            <Users className="h-4 w-4 mx-auto text-muted-foreground mb-1" />
            <p className="text-2xl font-bold">{summary.total}</p>
            <p className="text-[10px] text-muted-foreground">Workers</p>
          </CardContent></Card>
          <Card><CardContent className="p-3 text-center">
            <TrendingUp className="h-4 w-4 mx-auto text-muted-foreground mb-1" />
            <p className="text-2xl font-bold">{summary.avgLoad}%</p>
            <p className="text-[10px] text-muted-foreground">Avg Load</p>
          </CardContent></Card>
          <Card><CardContent className="p-3 text-center">
            <Truck className="h-4 w-4 mx-auto text-muted-foreground mb-1" />
            <p className="text-2xl font-bold">{Math.round(summary.totalTravel / 60)}h</p>
            <p className="text-[10px] text-muted-foreground">Total Travel</p>
          </CardContent></Card>
          <Card><CardContent className="p-3 text-center">
            <CalendarDays className="h-4 w-4 mx-auto text-muted-foreground mb-1" />
            <p className="text-2xl font-bold">{summary.totalJobs}</p>
            <p className="text-[10px] text-muted-foreground">Total Jobs</p>
          </CardContent></Card>
        </div>
      )}

      {/* Worker cards */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="grid gap-3" data-testid="workers-load-grid">
          {sortedWorkers.map(w => (
            <WorkerLoadCard key={w.worker_id} worker={w} />
          ))}
          {sortedWorkers.length === 0 && (
            <p className="text-center text-sm text-muted-foreground py-8">No worker data available</p>
          )}
        </div>
      )}

      {/* Complete Order dialog for pending drafts */}
      {pendingDraft && (
        <CompletePendingOrderDialog
          open={completeDialogOpen}
          onOpenChange={setCompleteDialogOpen}
          api={api}
          draft={pendingDraft}
          onCompleted={handlePendingOrderCompleted}
        />
      )}
    </div>
  );
}
